import {
  faMicrophone,
  faMicrophoneSlash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface LargeMicVisualizerProps {
  isActive: boolean;
  iconSize?: 'sm' | 'md' | 'lg';
}

export const LargeMicVisualizer = ({
  isActive,
  iconSize = 'md',
}: LargeMicVisualizerProps) => {
  // Size configurations matching MicLoadingState for visual consistency
  const sizeConfig = {
    sm: {
      container: 'vai-w-16 vai-h-16',
      outerCircle: 'vai-w-14 vai-h-14',
      middleCircle: 'vai-w-12 vai-h-12',
      innerCircle: 'vai-w-10 vai-h-10',
      iconContainer: 'vai-w-4 vai-h-4',
      iconFontSize: '0.5rem', // 8px
    },
    md: {
      container: 'vai-w-32 vai-h-32',
      outerCircle: 'vai-w-28 vai-h-28',
      middleCircle: 'vai-w-24 vai-h-24',
      innerCircle: 'vai-w-20 vai-h-20',
      iconContainer: 'vai-w-6 vai-h-6',
      iconFontSize: '0.75rem', // 12px
    },
    lg: {
      container: 'vai-w-40 vai-h-40',
      outerCircle: 'vai-w-36 vai-h-36',
      middleCircle: 'vai-w-30 vai-h-30',
      innerCircle: 'vai-w-24 vai-h-24',
      iconContainer: 'vai-w-8 vai-h-8',
      iconFontSize: '1rem', // 16px
    },
  };

  const sizes = sizeConfig[iconSize];

  return (
    <div className="vai-flex vai-items-center vai-gap-4 vai-p-6 vai-bg-white">
      {/* Microphone Icon */}
      <div className="vai-relative vai-flex-shrink-0">
        {isActive ? (
          // Connected/Active state - Green circles with microphone
          <div
            className={`vai-relative ${sizes.container} vai-flex vai-items-center vai-justify-center`}
          >
            {/* Outer ring - light mint green */}
            <div
              className={`vai-absolute ${sizes.outerCircle} vai-rounded-full vai-bg-green-300 vai-animate-pulse`}
              style={{ animationDuration: '1.5s' }}
            ></div>
            {/* Middle ring - medium mint green */}
            <div
              className={`vai-absolute ${sizes.middleCircle} vai-rounded-full vai-bg-green-400 vai-animate-pulse`}
              style={{
                animationDuration: '1.5s',
                animationDelay: '0.3s',
              }}
            ></div>
            {/* Inner circle - dark teal green */}
            <div
              className={`vai-relative ${sizes.innerCircle} vai-rounded-full vai-bg-green-700 vai-flex vai-items-center vai-justify-center vai-z-10`}
            >
              <div
                className={`${sizes.iconContainer} vai-flex vai-items-center vai-justify-center`}
              >
                <FontAwesomeIcon
                  icon={faMicrophone}
                  className="vai-text-white"
                  style={{ fontSize: sizes.iconFontSize }}
                />
              </div>
            </div>
          </div>
        ) : (
          // Disconnected/Muted state - Red circles with muted microphone
          <div
            className={`vai-relative ${sizes.container} vai-flex vai-items-center vai-justify-center`}
          >
            <div
              className={`vai-relative ${sizes.innerCircle} vai-rounded-full vai-bg-red-700 vai-flex vai-items-center vai-justify-center vai-z-10`}
            >
              <div
                className={`${sizes.iconContainer} vai-flex vai-items-center vai-justify-center`}
              >
                <FontAwesomeIcon
                  icon={faMicrophoneSlash}
                  className="vai-text-white"
                  style={{ fontSize: sizes.iconFontSize }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status Text */}
      <div className="vai-flex vai-flex-col vai-gap-1">
        {isActive ? (
          <>
            <p className="vai-font-bold vai-text-lg vai-text-gray-900">
              Microphone connected.
            </p>
            <p className="vai-text-sm vai-text-gray-600">Ready to record!</p>
          </>
        ) : (
          <>
            <p className="vai-font-bold vai-text-lg vai-text-gray-900">
              No active microphone found.
            </p>
            <p className="vai-text-sm vai-text-gray-600">
              Say something to activate the mic. If it&apos;s not responding,
              choose another mic.
            </p>
          </>
        )}
      </div>
    </div>
  );
};
